import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { TableauSiteRole, User } from "../../../../app/backendTypes";
import { Close } from "@mui/icons-material";
import {
  usePendingTableauSiteRoleChangeTask,
  useTableauUserSiteRole,
} from "../../../../app/hooks";
import Loading from "../../Loading";
import Error from "../../Error";
import PendingAction from "../PendingAction";
import UserSpan from "../../users/UserSpan";
import { useState } from "react";
import { TableauSiteRoleChangeDialog } from "./TableauSiteRoleChangeDialog";

export default function TableauSiteRoleDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  user: User;
  onRoleChange: () => void;
}) {
  const { open, setOpen, user, onRoleChange } = props;

  const [role, setRole] = useState<TableauSiteRole | null>(null);

  const {
    role: currentRole,
    error: currentRoleError,
    loading: currentRoleLoading,
    reload: currentRoleReload,
  } = useTableauUserSiteRole(user.employee.id);

  const {
    task: pendingTask,
    error: pendingError,
    loading: pendingLoading,
    reload: pendingReload,
  } = usePendingTableauSiteRoleChangeTask(user.employee.id);

  let currentRoleElement = <></>;
  if (currentRoleLoading) currentRoleElement = <Loading />;
  else if (currentRoleError)
    currentRoleElement = <Error error={currentRoleError} />;
  else if (currentRole !== null) currentRoleElement = <>{currentRole}</>;

  let pendingElement = <></>;
  if (pendingLoading) pendingElement = <Loading />;
  else if (pendingError) pendingElement = <Error error={pendingError} />;
  else if (pendingTask !== null)
    pendingElement = (
      <>
        <Typography>
          User has a pending request for changing their site role
        </Typography>

        <br />

        <PendingAction
          task={pendingTask}
          callback={pendingReload}
          onCompleted={() => {
            pendingReload();
            currentRoleReload();
            onRoleChange();
          }}
        />
      </>
    );

  return (
    <>
      <Dialog open={open}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="h6" color="primary">
              Change site role in Tableau
            </Typography>
            <IconButton
              onClick={() => {
                setOpen(false);
              }}
            >
              <Close />
            </IconButton>
          </Box>

          <br />

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography color="text.secondary">For user: </Typography>
            <UserSpan user={user} />
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography color="text.secondary">Current role: </Typography>
            {currentRoleElement}
          </Box>

          <br />
          {pendingElement}

          {pendingTask === null && (
            <>
              <br />

              <FormControl fullWidth sx={{ maxWidth: "sm" }}>
                <InputLabel id="new-site-role" size="small">
                  New site role
                </InputLabel>

                <Select
                  disabled={
                    pendingLoading ||
                    currentRoleLoading ||
                    Boolean(pendingError) ||
                    Boolean(currentRoleError)
                  }
                  value={role || ""}
                  onChange={(e) => setRole(e.target.value as TableauSiteRole)}
                  label="New site role"
                  labelId="new-site-role"
                  size="small"
                >
                  {Object.values(TableauSiteRole)
                    .filter(
                      (r) =>
                        r !== currentRole &&
                        r !== TableauSiteRole.ExplorerCanPublish &&
                        r !== TableauSiteRole.SiteAdministratorExplorer &&
                        r !== TableauSiteRole.SiteAdministratorCreator &&
                        Object.values(TableauSiteRole).findIndex((role) => role === r) > Object.values(TableauSiteRole).findIndex((role) => role === currentRole)
                    )
                    .map((role, i) => (
                      <MenuItem key={i} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <br />
              <br />

              <Typography color="text.primary">
                Note: It can take up to 1 hour for the changes to take effect in Tableau
              </Typography>

              <br />
              <br />


              <TableauSiteRoleChangeButton
                role={role}
                user={user}
                disabled={
                  currentRoleLoading || pendingLoading || role === currentRole
                }
                callback={pendingReload}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function TableauSiteRoleChangeButton(props: {
  role: TableauSiteRole | null;
  user: User;
  disabled: boolean;
  callback: () => void;
}) {
  const { role, user, disabled, callback } = props;

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        disabled={disabled || role === null}
        onClick={() => setOpen(true)}
      >
        Request change
      </Button>

      <TableauSiteRoleChangeDialog
        open={open}
        setOpen={setOpen}
        role={role}
        user={user}
        callback={callback}
      />
    </>
  );
}
